import { Box, Stack, Typography, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';

// ──────────────────────────────────────────────────────────────────────────────
// WizardSection — the shared subsection container for every wizard stage.
// Gives each subsection an unmistakable identity (icon tile + title + helper +
// optional badge/action) inside a self-contained outlined card, so a stage reads
// as distinct, scannable blocks instead of one long document.
//
// Styling is centralised in the `sx` style-functions below (no per-call inline
// repetition) and every colour is pulled from the theme palette — never hardcoded.
// Icons follow the wizard's existing emoji convention (e.g. "📝", "🔌") but a
// node is accepted too.
// ──────────────────────────────────────────────────────────────────────────────

const sx = {
  card: (override) => ({
    bgcolor: 'background.paper',
    border: 1,
    borderColor: 'divider',
    borderRadius: 2,
    boxShadow: (t) => t.customShadows.z1,
    overflow: 'hidden',
    ...override,
  }),
  header: {
    px: 2.25,
    py: 1.5,
    bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
    borderBottom: 1,
    borderColor: 'divider',
  },
  iconTile: {
    width: 34,
    height: 34,
    borderRadius: '10px',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: 'primary.lighter',
    color: 'primary.dark',
    fontSize: 17,
    lineHeight: 1,
  },
  title: { fontWeight: 700, fontSize: 14.5, lineHeight: 1.2, color: 'text.primary' },
  desc: { fontSize: 12, color: 'text.secondary', mt: 0.25, lineHeight: 1.35 },
  badge: {
    height: 20,
    fontSize: 10.5,
    fontWeight: 700,
    bgcolor: 'primary.lighter',
    color: 'primary.dark',
    '& .MuiChip-label': { px: 1 },
  },
  body: (override) => ({ p: 2.25, ...override }),
};

export default function WizardSection({
  icon,
  title,
  description,
  badge,
  action,
  children,
  sx: cardSx,
  bodySx,
}) {
  return (
    <Box sx={sx.card(cardSx)}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={sx.header}>
        {icon != null && <Box sx={sx.iconTile}>{icon}</Box>}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={sx.title}>{title}</Typography>
          {description && <Typography sx={sx.desc}>{description}</Typography>}
        </Box>
        {action || (badge && <Chip size="small" label={badge} sx={sx.badge} />)}
      </Stack>
      <Box sx={sx.body(bodySx)}>{children}</Box>
    </Box>
  );
}
